import { IllegalArgumentException } from '../../Cause/dist/effect-Cause.esm.js';
import { uninterruptibleMask, zipRight, ensuring, acquireReleaseInterruptible } from '../../Effect/dist/effect-Effect.esm.js';
import { dual } from '../../Function/dist/effect-Function.esm.js';
import { withSTMRuntime, commit } from './core.esm.js';
import { make as make$1, unsafeGet, unsafeSet, get, TRefImpl } from './tRef.esm.js';
import { map, retry, succeed } from '../../STM/dist/effect-STM.esm.js';

/** @internal */
const TSemaphoreSymbolKey = "effect/TSemaphore";

/** @internal */
const TSemaphoreTypeId = /*#__PURE__*/Symbol.for(TSemaphoreSymbolKey);

/** @internal */
class TSemaphoreImpl {
  [TSemaphoreTypeId] = TSemaphoreTypeId;
  constructor(permits) {
    this.permits = permits;
  }
}

/** @internal */
const make = permits => map(make$1(permits), permits => new TSemaphoreImpl(permits));

/** @internal */
const acquire = self => acquireN(self, 1);

/** @internal */
const acquireN = /*#__PURE__*/dual(2, (self, n) => withSTMRuntime(driver => {
  if (n < 0) {
    throw IllegalArgumentException(`Unexpected negative value ${n} passed to Semaphore.acquireN`);
  }
  const value = unsafeGet(self.permits, driver.journal);
  if (value < n) {
    return retry;
  } else {
    return succeed(unsafeSet(self.permits, value - n, driver.journal));
  }
}));

/** @internal */
const available = self => get(self.permits);

/** @internal */
const release = self => releaseN(self, 1);

/** @internal */
const releaseN = /*#__PURE__*/dual(2, (self, n) => withSTMRuntime(driver => {
  if (n < 0) {
    throw IllegalArgumentException(`Unexpected negative value ${n} passed to Semaphore.releaseN`);
  }
  const current = unsafeGet(self.permits, driver.journal);
  return succeed(unsafeSet(self.permits, current + n, driver.journal));
}));

/** @internal */
const withPermit = /*#__PURE__*/dual(2, (self, semaphore) => withPermits(self, semaphore, 1));

/** @internal */
const withPermits = /*#__PURE__*/dual(3, (self, semaphore, permits) => uninterruptibleMask(restore => zipRight(restore(commit(acquireN(permits)(semaphore))), ensuring(self, commit(releaseN(permits)(semaphore))))));

/** @internal */
const withPermitScoped = self => withPermitsScoped(self, 1);

/** @internal */
const withPermitsScoped = /*#__PURE__*/dual(2, (self, permits) => acquireReleaseInterruptible(commit(acquireN(self, permits)), () => commit(releaseN(self, permits))));

/** @internal */
const unsafeMakeSemaphore = permits => {
  return new TSemaphoreImpl(new TRefImpl(permits));
};

export { TSemaphoreTypeId, acquire, acquireN, available, make, release, releaseN, unsafeMakeSemaphore, withPermit, withPermitScoped, withPermits, withPermitsScoped };
